import { CreditMap, LoanEntry, ComparisonReport, InstitutionComparison, LoanComparison } from './types';

/** Build a content key for grouping loans that look identical */
function loanKey(l: LoanEntry): string {
  return `${l.produtoFinanceiro}|${l.inicio}|${l.fim}`;
}

/**
 * Match loans from two lists positionally within each group of identical keys.
 * ALL loans from both lists must appear in the result — no loan is ever dropped.
 * Loans that exist only in anterior → status 'liquidado'
 * Loans that exist only in atual    → status 'novo' * Loans present in both             → status'activo' or 'alterado'
 */
function matchLoans(
  loansAnt: LoanEntry[],
  loansAtu: LoanEntry[]
): LoanComparison[] {
  // Group by content key, preserving insertion order
  const groupAnt = new Map<string, LoanEntry[]>();
  for (const l of loansAnt) {
    const k = loanKey(l);
    if (!groupAnt.has(k)) groupAnt.set(k, []);
    groupAnt.get(k)!.push(l);
  }

  const groupAtu = new Map<string, LoanEntry[]>();
  for (const l of loansAtu) {
    const k = loanKey(l);
    if (!groupAtu.has(k)) groupAtu.set(k, []);
    groupAtu.get(k)!.push(l);
  }

  // Track which loans have been consumed so we never drop any
  const usedAnt = new Set<string>(); // loanEntry.id
  const usedAtu = new Set<string>();

  const result: LoanComparison[] = [];

  // Pass 1: match by key positionally
  const allKeys = new Set([...groupAnt.keys(), ...groupAtu.keys()]);

  for (const key of allKeys) {
    const antList = groupAnt.get(key) ?? [];
    const atuList = groupAtu.get(key) ?? [];
    const maxLen = Math.max(antList.length, atuList.length);

    for (let idx = 0; idx < maxLen; idx++) {
      const lAnt = antList[idx] ?? null;
      const lAtu = atuList[idx] ?? null;
      const ref = lAnt ?? lAtu!;

      if (lAnt) usedAnt.add(lAnt.id);
      if (lAtu) usedAtu.add(lAtu.id);

      let loanStatus: LoanComparison['status'] = 'activo';
      if (!lAnt) loanStatus = 'novo';
      else if (!lAtu) loanStatus = 'liquidado';
      else if (lAtu.totalEmDivida !== lAnt.totalEmDivida) loanStatus = 'alterado';

      result.push({
        loanId: ref.id,
        produtoFinanceiro: ref.produtoFinanceiro,
        inicio: ref.inicio,
        fim: ref.fim,
        saldoAnterior: lAnt?.totalEmDivida ?? null,
        saldoAtual: lAtu?.totalEmDivida ?? null,
        diferenca: (lAnt?.totalEmDivida ?? 0) - (lAtu?.totalEmDivida ?? 0),
        status: loanStatus,
      });
    }
  }

  // Pass 2: safety net — append any loans not yet consumed (should not happen
  // with the above logic, but guarantees no loan is ever silently dropped)
  for (const l of loansAnt) {
    if (!usedAnt.has(l.id)) {
      result.push({
        loanId: l.id,
        produtoFinanceiro: l.produtoFinanceiro,
        inicio: l.inicio,
        fim: l.fim,
        saldoAnterior: l.totalEmDivida,
        saldoAtual: null,
        diferenca: l.totalEmDivida,
        status: 'liquidado',
      });
    }
  }
  for (const l of loansAtu) {
    if (!usedAtu.has(l.id)) {
      result.push({
        loanId: l.id,
        produtoFinanceiro: l.produtoFinanceiro,
        inicio: l.inicio,
        fim: l.fim,
        saldoAnterior: null,
        saldoAtual: l.totalEmDivida,
        diferenca: -l.totalEmDivida,
        status: 'novo',
      });
    }
  }

  return result;
}

export function buildComparisonReport(mapAnterior: CreditMap, mapAtual: CreditMap): ComparisonReport {
  const institutionsAnterior = new Map(mapAnterior.institutions.map((i) => [i.nome, i]));
  const institutionsAtual = new Map(mapAtual.institutions.map((i) => [i.nome, i]));

  const allNames = new Set([...institutionsAnterior.keys(), ...institutionsAtual.keys()]);

  const institutions: InstitutionComparison[] = [];

  for (const nome of allNames) {
    const instAnt = institutionsAnterior.get(nome) ?? null;
    const instAtu = institutionsAtual.get(nome) ?? null;

    const saldoAnterior = instAnt?.totalEmDivida ?? 0;
    const saldoAtual = instAtu?.totalEmDivida ?? 0;
    const valorPago = saldoAnterior - saldoAtual;
    const variacaoPercent = saldoAnterior > 0 ? ((saldoAtual - saldoAnterior) / saldoAnterior) * 100 : 0;

    let status: InstitutionComparison['status'] = 'match';
    if (!instAnt) status = 'novo';
    else if (!instAtu) status = 'ausente';
    else if (saldoAtual > saldoAnterior) status = 'anomalia';

    // Match loans positionally within groups — no loan is ever dropped
    const loans = matchLoans(instAnt?.loans ?? [], instAtu?.loans ?? []);

    institutions.push({
      institutionId: instAnt?.id ?? instAtu?.id ?? nome,
      nome,
      saldoAnterior,
      saldoAtual,
      valorPago,
      variacaoPercent,
      numEmprestimosAnterior: instAnt?.loans.length ?? 0,
      numEmprestimosAtual: instAtu?.loans.length ?? 0,
      novosEmprestimos: loans.filter((l) => l.status === 'novo').length,
      emprestimosLiquidados: loans.filter((l) => l.status === 'liquidado').length,
      status,
      loans,
    });
  }

  // Sort by valorPago descending
  institutions.sort((a, b) => b.valorPago - a.valorPago);

  const totalSaldoAnterior = institutions.reduce((s, i) => s + i.saldoAnterior, 0);
  const totalSaldoAtual = institutions.reduce((s, i) => s + i.saldoAtual, 0);
  const numInstituicoesMatch = institutions.filter((i) => i.status === 'match').length;

  return {
    mesAnterior: mapAnterior.mesLabel,
    mesAtual: mapAtual.mesLabel,
    totalSaldoAnterior,
    totalSaldoAtual,
    totalValorPago: totalSaldoAnterior - totalSaldoAtual,
    numInstituicoes: institutions.length,
    numInstituicoesMatch,
    numInstituicoesNovas: institutions.filter((i) => i.status === 'novo').length,
    numInstituicoesAusentes: institutions.filter((i) => i.status === 'ausente').length,
    institutions,
    generatedAt: new Date().toISOString(),
  };
}
